import localforage from "localforage";
import _ from "lodash";
import { _showAppLogs } from "../LogContext";
import { createRefPath } from "global/firestoreData/appData/appRefPaths";
import { ParentContext } from "../ParentContext";
import { useCallback, useContext } from "react";
import { useAppUserAccess } from "./useAppUserAccess";

const deepMergeReplaceArrays = (target, source) =>
  _.mergeWith({}, target, source, (objValue, srcValue) => {
    if (_.isArray(srcValue)) {
      return srcValue;
    }
  });

export const useCacheDataManager = ({ fsdb } = {}) => {

  const _appUserAccess = useAppUserAccess()
  const { appUserSession } = _appUserAccess ?? {}

  const parentContext = useContext(ParentContext);
  const { fns } = parentContext ?? {}
  const { database_fns } = fns ?? {}

  const _db = database_fns || fsdb

  const getGroup = useCallback(async (groupKey) => {
    const cached = await localforage.getItem(groupKey)
    return cached?.data || {}
  }, [])

  const getSubItem = useCallback(async (groupKey, subKey) => {
    const group = await getGroup(groupKey);
    return group?.[subKey] || null;
  }, [])

  const setGroup = useCallback(async (groupKey, newGroupData) => {
    await localforage.setItem(groupKey, { data: newGroupData, timestamp: Date.now() });
  }, [])

  const removeFromGroup = useCallback(async (groupKey, subKey) => {
    const cached = await localforage.getItem(groupKey);
    if (!cached) return;

    const updated = _.omit(cached.data, subKey);
    await localforage.setItem(groupKey, { data: updated, timestamp: Date.now() });
  }, [])

  const deleteGroup = useCallback(async (groupKey) => {
    await localforage.removeItem(groupKey);
  }, [])

  const mergeToGroup = useCallback(async (groupKey, subKey, newData, ts, forceUpdate) => {

    // get the existing data from cache
    const cached = await localforage.getItem(groupKey);
    const existing = cached?.data || {};
    const existingSub = existing[subKey] || {};

    // Merge sub-level data (arrays replaced)
    const updatedSub = deepMergeReplaceArrays(existingSub, newData);

    // Compare ONLY data (exclude timestamp)
    const cacheEqual = _.isEqual(existingSub, updatedSub);

    const ts_update = ts ?? Date.now();

    // Always add timestamp AFTER comparison
    const updatedSubWithTimestamp = {
      ...updatedSub,
      _timestamp: ts_update,
    };

    // Merge back at the correct level
    const updated = deepMergeReplaceArrays(existing, {
      [subKey]: updatedSubWithTimestamp,
    });

    if (!cacheEqual || forceUpdate) {

      const newCache = { data: updated, _timestamp: ts_update };

      if (_db) {
        const _docName = groupKey;

        // update the timpstamp in the database
        if (_appUserAccess) {
          const _refPath = createRefPath(['_timeStamps', 'ts']);
          const _dataToUpdate = { [_docName]: { [subKey]: ts_update } }
          // console.log('_refPath', _refPath, _dataToUpdate)
          _db.doc_set(_refPath, _dataToUpdate, true)
        }
      }

      // console.log('groupKey, newCache', groupKey, newCache)

      await localforage.setItem(groupKey, newCache);
      _showAppLogs.cache && console.log(`🟢 Merged ${groupKey}/${subKey}`, ts_update, newData);

      return newCache.data;
    }

    return existing;
  }, [])

  return { deleteGroup, getGroup, getSubItem, removeFromGroup, setGroup, mergeToGroup }
}

/**
 * Grouped cache manager for app/client/event data.
 * Supports nesting by groupKey (e.g., "clientInfo") and subKey (e.g., "ARM").
 */
export const cacheDataManager = {

  async getGroup(groupKey) {
    const cached = await localforage.getItem(groupKey);
    return cached?.data || {};
  },

  async getSubItem(groupKey, subKey) {
    const group = await this.getGroup(groupKey);
    return group?.[subKey] || null;
  },

  async mergeToGroup(groupKey, subKey, newData, ts, database_fns, forceUpdate) {

    // get the existing data from cache
    const cached = await localforage.getItem(groupKey);
    const existing = cached?.data || {};
    const existingSub = existing[subKey] || {};

    // Merge sub-level data (arrays replaced)
    const updatedSub = deepMergeReplaceArrays(existingSub, newData);

    // Compare ONLY data (exclude timestamp)
    const cacheEqual = _.isEqual(existingSub, updatedSub);

    const ts_update = ts ?? Date.now();

    // Always add timestamp AFTER comparison
    const updatedSubWithTimestamp = {
      ...updatedSub,
      _timestamp: ts_update,
    };

    // Merge back at the correct level
    const updated = deepMergeReplaceArrays(existing, {
      [subKey]: updatedSubWithTimestamp,
    });

    if (!cacheEqual || forceUpdate) {
      const newCache = {
        data: updated,
        _timestamp: ts_update,
      };

      if (database_fns) {
        const _docName = groupKey;

        // update the timpstamp in the datbase
        const _refPath = createRefPath(['_timeStamps', 'ts']);
        const _dataToUpdate = {
          [_docName]: {
            [subKey]: ts_update
          }
        }
        console.log('_refPath', _refPath, _dataToUpdate)
        database_fns.doc_set(_refPath, _dataToUpdate, true)
      }

      await localforage.setItem(groupKey, newCache);
      _showAppLogs.cache && console.log(`🟢 Merged ${groupKey}/${subKey}`, ts_update, newData);

      return newCache.data;
    }

    return existing;
  },
};
